import { Location } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { CanDeactivateComponent } from '@app/interfaces';
import { JobFunnel } from '@app/models/recruiting/job-funnel.model';
import {
    ApplicantIndividualTestMessage,
    BulkApplicantMessage,
} from '@app/modules/applicant-tracker/interfaces/bulk-applicant-message';
import { ApplicantMessageService } from '@app/modules/applicant-tracker/services';
import { BulkApplicantMessageForm } from '@app/modules/forms/applicant-tracker/components';
import { AnalyticService, AuthService, NotifyService } from '@app/services';

@Component({
    templateUrl: './bulk-applicant-message-store.template.html',
})
export class BulkApplicantMessageStoreView implements CanDeactivateComponent {
    isLoading = false;
    jobPostingId: number;
    isConfirmBulkEmailModalOpen = false;
    isDeactivateModalOpen = false;
    canNavigateBack = true;
    previousPath: string;

    bulkApplicantMessage: BulkApplicantMessage = {
        isBulk: true,
        replyTo: this.auth.account.email,
        subject: '',
        body: '',
        atsJobFunnelId: null,
    };

    constructor(
        private auth: AuthService,
        private route: ActivatedRoute,
        private applicantMessageService: ApplicantMessageService,
        private notifyService: NotifyService,
        private location: Location,
        private router: Router,
        private analyticService: AnalyticService
    ) {
        this.jobPostingId = +this.route.snapshot.params?.jobPostingId || null;
    }

    canDeactivate(): boolean {
        if (this.bulkApplicantMessage.body) {
            this.canNavigateBack = false;
            this.isDeactivateModalOpen = true;
            this.previousPath = this.previousPath ?? this.location.path();
        }

        return this.canNavigateBack;
    }

    canNavigateAway(): void {
        this.canNavigateBack = true;
        this.bulkApplicantMessage.body = '';
        this.router.navigateByUrl(this.previousPath);
    }

    openConfirmBulkEmail(): void {
        this.isConfirmBulkEmailModalOpen = true;
    }

    closeConfirmBulkEmail(): void {
        this.isConfirmBulkEmailModalOpen = false;
    }

    openDeactivateModalOpen(): void {
        this.isDeactivateModalOpen = true;
    }

    closeDeactivateModalOpen(): void {
        this.isDeactivateModalOpen = false;
    }

    async onSave(applicantMessageForm: BulkApplicantMessageForm): Promise<void> {
        applicantMessageForm.submit();

        if (!applicantMessageForm.valid) {
            this.closeConfirmBulkEmail();
            return;
        }

        // Check if a job funnel has any applicants
        const noFunnelApplicants = !applicantMessageForm.selectedJobPosting.jobFunnels.find((jobFunnel: JobFunnel) => {
            return jobFunnel.id === this.bulkApplicantMessage.atsJobFunnelId;
        }).activeApplicants.length;

        if (noFunnelApplicants) {
            this.closeConfirmBulkEmail();
            this.notifyService.error('applicant-tracker.selectJobFunnelHasNoApplicants');
            return;
        }

        this.isLoading = true;

        try {
            await this.applicantMessageService.onBulkSendApplicantMessage(this.bulkApplicantMessage);
            this.notifyService.success('applicant-tracker.emailSent');
            this.analyticService.trackEvent(AnalyticEvents.RecruitingEmailsAllBulkEmailsSend);
            this.bulkApplicantMessage.body = '';
            this.canNavigateBack = true;
            this.isLoading = false;
            this.previousPath ? this.router.navigateByUrl(this.previousPath) : this.location.back();
        } catch (err) {
            this.notifyService.error('applicant-tracker.emailFailedToSend');
            this.isLoading = false;
        }

        this.closeConfirmBulkEmail();
    }

    async onSendTest(applicantMessageForm: BulkApplicantMessageForm): Promise<void> {
        applicantMessageForm.submit();

        if (!applicantMessageForm.valid) {
            return;
        }

        const bulkApplicantTestMessage: ApplicantIndividualTestMessage = {
            atsJobId: this.jobPostingId,
            body: this.bulkApplicantMessage.body,
            replyTo: this.bulkApplicantMessage.replyTo,
            subject: this.bulkApplicantMessage.subject,
        };

        this.isLoading = true;

        try {
            await this.applicantMessageService.onApplicantIndividualTestMessage(bulkApplicantTestMessage);
            this.notifyService.success({
                key: 'applicant-tracker.testEmailSentTo',
                params: {
                    email: this.bulkApplicantMessage.replyTo,
                },
            });
            this.isLoading = false;
            this.analyticService.trackEvent(AnalyticEvents.RecruitingAllEmailsSendTest);
        } catch (err) {
            this.notifyService.error({
                key: 'applicant-tracker.testEmailFailedToSendTo',
                params: {
                    email: this.bulkApplicantMessage.replyTo,
                },
            });
            this.isLoading = false;
        }
    }
}
